import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    CartesianGrid,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts'

import { api, LinkedInClusterMap, LinkedInClusterMapPoint } from '../../../services/api'

/**
 * The topic clusters drawn on a 2D plane.
 *
 * Every post is a point; posts about the same thing sit close together and share
 * a colour, and each cluster is labelled at the mean position of its posts. The
 * axes are a projection of the Gemini embeddings (PCA), so they carry no unit:
 * only the distances between points mean something.
 */

/** Distinguishable on white and in colour-blind simulations; cycled for more clusters. */
export const CLUSTER_PALETTE = [
    '#1f77b4',
    '#e8710a',
    '#2ca02c',
    '#d62728',
    '#9467bd',
    '#8c564b',
    '#e377c2',
    '#17becf',
    '#bcbd22',
    '#0b5394',
    '#a64d79',
    '#38761d',
]
export const UNCLUSTERED_COLOR = '#9e9e9e'

export const clusterColor = (index: number): string => CLUSTER_PALETTE[index % CLUSTER_PALETTE.length]

/** Points of one cluster (``null`` = the ones without a cluster), in chart form. */
export const pointsOf = (points: LinkedInClusterMapPoint[], clusterId: number | null) =>
    points.filter((p) => p.cluster_id === clusterId)

const Wrap = styled.div`
    display: grid;
    grid-template-columns: 1fr minmax(180px, 260px);
    gap: ${({ theme }) => theme.spacing.md};

    @media (max-width: 820px) {
        grid-template-columns: 1fr;
    }
`

const Card = styled.div`
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: 0.8rem;
`

const Legend = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    max-height: 520px;
    overflow-y: auto;
`

const LegendItem = styled.button<{ $active: boolean; $dim: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    text-align: left;
    border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
    background: transparent;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    padding: 0.3rem 0.4rem;
    font: inherit;
    font-size: 0.82rem;
    cursor: pointer;
    opacity: ${({ $dim }) => ($dim ? 0.45 : 1)};

    &:hover {
        background: rgba(0, 0, 0, 0.05);
    }
`

const Swatch = styled.span<{ $color: string }>`
    flex: none;
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    background: ${({ $color }) => $color};
`

const Note = styled.p`
    margin: 0.4rem 0 0;
    font-size: 0.78rem;
    opacity: 0.75;
`

const TooltipBox = styled.div`
    max-width: 280px;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 6px;
    padding: 0.5rem 0.7rem;
    font-size: 0.8rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
`

interface Props {
    /** Opens the shared detail modal for a post. */
    onOpenPost?: (postId: number) => void
    /** Highlights (and lists) this cluster. */
    selectedClusterId?: number | null
    onSelectCluster?: (clusterId: number) => void
}

export const ClusterMap: React.FC<Props> = ({ onOpenPost, selectedClusterId = null, onSelectCluster }) => {
    const [map, setMap] = useState<LinkedInClusterMap | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false
        api.linkedin
            .getClusterMap()
            .then((res) => !cancelled && setMap(res.data))
            .catch(() => !cancelled && setError('No se pudo cargar el mapa de clusteres.'))
            .finally(() => !cancelled && setLoading(false))
        return () => {
            cancelled = true
        }
    }, [])

    const clusters = useMemo(() => (map?.clusters ?? []).filter((c) => c.points > 0), [map])
    const colorById = useMemo(() => {
        const out = new Map<number, string>()
        clusters.forEach((c, i) => out.set(c.id, clusterColor(i)))
        return out
    }, [clusters])

    if (loading) return <Note>Cargando mapa…</Note>
    if (error) return <Note>{error}</Note>
    if (!map || !map.available) return <Note>{map?.message || 'El mapa no está disponible.'}</Note>
    if (map.points.length < 2) {
        return <Note>Hacen falta al menos dos publicaciones con vector para dibujar el mapa.</Note>
    }

    const unclustered = pointsOf(map.points, null)
    const dimmed = (id: number | null) => selectedClusterId !== null && id !== selectedClusterId
    const variance = (map.explained_variance ?? []).reduce((a, b) => a + b, 0)

    const renderTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: LinkedInClusterMapPoint }> }) => {
        const p = active && payload && payload.length ? payload[0].payload : null
        if (!p) return null
        const cluster = clusters.find((c) => c.id === p.cluster_id)
        return (
            <TooltipBox>
                <strong>{p.author || 'Autor desconocido'}</strong>
                <div>{p.summary || 'Sin resumen'}</div>
                <div style={{ opacity: 0.75, marginTop: 4 }}>
                    {cluster ? cluster.name : 'Sin cluster'}
                    {typeof p.reactions === 'number' ? ` · ${p.reactions} reacciones` : ''}
                </div>
            </TooltipBox>
        )
    }

    const dot = (color: string, id: number | null) => (props: { cx?: number; cy?: number; payload?: LinkedInClusterMapPoint }) => {
        const { cx = 0, cy = 0, payload } = props
        const selected = id !== null && id === selectedClusterId
        return (
            <circle
                cx={cx}
                cy={cy}
                r={selected ? 8 : 6}
                fill={color}
                fillOpacity={dimmed(id) ? 0.2 : 0.85}
                stroke={selected ? '#000' : '#fff'}
                strokeWidth={selected ? 1.5 : 1}
                style={{ cursor: onOpenPost ? 'pointer' : 'default' }}
                data-post-id={payload?.post_id}
                onClick={() => payload && onOpenPost?.(payload.post_id)}
            />
        )
    }

    // Cluster names at their centroid, only for the ones worth a label.
    const labelled = clusters.filter((c) => c.centroid && c.points >= 2)

    return (
        <Wrap>
            <Card>
                <ResponsiveContainer width="100%" height={520}>
                    <ScatterChart margin={{ top: 16, right: 24, bottom: 16, left: 24 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                        <XAxis type="number" dataKey="x" domain={[-1.1, 1.1]} hide />
                        <YAxis type="number" dataKey="y" domain={[-1.1, 1.1]} hide />
                        <ZAxis range={[60, 60]} />
                        <Tooltip content={renderTooltip as never} cursor={{ strokeDasharray: '3 3' }} />
                        {unclustered.length > 0 && (
                            <Scatter
                                name="Sin cluster"
                                data={unclustered}
                                shape={dot(UNCLUSTERED_COLOR, null) as never}
                                isAnimationActive={false}
                            />
                        )}
                        {clusters.map((c) => (
                            <Scatter
                                key={c.id}
                                name={c.name}
                                data={pointsOf(map.points, c.id)}
                                shape={dot(colorById.get(c.id) ?? UNCLUSTERED_COLOR, c.id) as never}
                                isAnimationActive={false}
                            />
                        ))}
                        <Scatter
                            name="Etiquetas"
                            data={labelled.map((c) => ({ x: c.centroid!.x, y: c.centroid!.y, id: c.id, name: c.name }))}
                            legendType="none"
                            isAnimationActive={false}
                            shape={((props: { cx?: number; cy?: number; payload?: { id: number; name: string } }) => {
                                const { cx = 0, cy = 0, payload } = props
                                if (!payload) return <g />
                                return (
                                    <text
                                        x={cx}
                                        y={cy - 12}
                                        textAnchor="middle"
                                        fontSize={11}
                                        fontWeight={600}
                                        fill="#222"
                                        stroke="#fff"
                                        strokeWidth={3}
                                        paintOrder="stroke"
                                        opacity={dimmed(payload.id) ? 0.25 : 1}
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        {payload.name.length > 28 ? `${payload.name.slice(0, 27)}…` : payload.name}
                                    </text>
                                )
                            }) as never}
                        />
                    </ScatterChart>
                </ResponsiveContainer>
                <Note>
                    Cada punto es una publicación; las cercanas hablan de lo mismo. Los ejes no tienen unidad: son una
                    proyección de los vectores de significado
                    {variance > 0 ? ` (conserva el ${(variance * 100).toFixed(0)} % de la información)` : ''}. Pulsa un
                    punto para abrir la publicación.
                </Note>
            </Card>

            <Card>
                <Legend>
                    {clusters.map((c) => (
                        <LegendItem
                            key={c.id}
                            type="button"
                            $active={selectedClusterId === c.id}
                            $dim={dimmed(c.id)}
                            onClick={() => onSelectCluster?.(c.id)}
                        >
                            <Swatch $color={colorById.get(c.id) ?? UNCLUSTERED_COLOR} />
                            <span>
                                {c.name} <span style={{ opacity: 0.65 }}>· {c.points}</span>
                            </span>
                        </LegendItem>
                    ))}
                    {unclustered.length > 0 && (
                        <LegendItem type="button" $active={false} $dim={selectedClusterId !== null} disabled>
                            <Swatch $color={UNCLUSTERED_COLOR} />
                            <span>
                                Sin cluster <span style={{ opacity: 0.65 }}>· {unclustered.length}</span>
                            </span>
                        </LegendItem>
                    )}
                </Legend>
            </Card>
        </Wrap>
    )
}

export default ClusterMap
